// Camera-based barcode scanner using @zxing/browser. Renders a fullscreen
// modal with a live camera preview; calls onDetect with the decoded text the
// first time a barcode is found, then stops the camera.
//
// Tuning notes (minimal pass — see "Barcode scanner hardening" in
// docs/HANDOFF.md backlog for the deferred follow-ups):
//   • POSSIBLE_FORMATS is restricted to retail product symbologies so each
//     decode attempt is cheap (the default multi-format reader also tries
//     QR / Data Matrix / PDF417 / Aztec on every frame).
//   • delayBetweenScanAttempts is dropped from the library default of 500ms
//     to 120ms — that 500ms idle gap between frames is most of the "why is
//     this so slow" feeling, especially while autofocus is still settling.
//   • We ask for a 1280×720-ideal stream and continuous autofocus (both
//     best-effort; ignored where the browser/camera doesn't support them).

import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader, type IScannerControls } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";

interface Props {
  open: boolean;
  onClose: () => void;
  onDetect: (barcode: string) => void;
}

// Symbologies actually used on grocery packaging. EAN/UPC cover almost
// everything; CODE_128 and ITF show up on some shipper/case labels.
const PRODUCT_FORMATS = [
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.CODE_128,
  BarcodeFormat.ITF,
];

export function BarcodeScanner({ open, onClose, onDetect }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  // Keep the latest onDetect in a ref so an unrelated parent re-render (e.g.
  // an async state update on the page hosting this modal) doesn't tear down
  // and restart the camera.
  const onDetectRef = useRef(onDetect);
  onDetectRef.current = onDetect;
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    const hints = new Map<DecodeHintType, unknown>();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, PRODUCT_FORMATS);
    const reader = new BrowserMultiFormatReader(hints, {
      delayBetweenScanAttempts: 120,
    });
    let cancelled = false;

    async function start() {
      try {
        // Prefer the rear camera on mobile and ask for a reasonably sharp
        // stream — a 480p feed makes thin barcode bars unresolvable.
        const constraints: MediaStreamConstraints = {
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        };
        const controls = await reader.decodeFromConstraints(
          constraints,
          videoRef.current!,
          (result) => {
            if (cancelled) return;
            if (result) {
              const text = result.getText();
              controls.stop();
              onDetectRef.current(text);
            }
            // Per-frame "not found" errors fire constantly and are expected;
            // nothing to do with them.
          }
        );
        controlsRef.current = controls;

        // Best-effort: nudge the live track into continuous autofocus. Not in
        // the standard MediaTrackConstraints type and unsupported on plenty of
        // browsers/cameras, so cast through unknown and swallow failures.
        const track = (videoRef.current?.srcObject as MediaStream | null)
          ?.getVideoTracks?.()[0];
        if (track) {
          try {
            await track.applyConstraints({
              advanced: [{ focusMode: "continuous" }],
            } as unknown as MediaTrackConstraints);
          } catch {
            /* focusMode unsupported here — fine */
          }
        }
      } catch (e) {
        setError(
          e instanceof Error
            ? e.message
            : "Couldn't access the camera. Please grant permission and try again."
        );
      }
    }

    void start();
    return () => {
      cancelled = true;
      controlsRef.current?.stop();
      controlsRef.current = null;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black text-white">
      <div className="flex items-center justify-between p-4">
        <h2 className="text-lg font-semibold">Scan a barcode</h2>
        <button
          onClick={onClose}
          className="rounded-full bg-white/10 px-4 py-2 text-sm font-medium hover:bg-white/20"
        >
          Cancel
        </button>
      </div>
      <div className="relative flex-1 overflow-hidden">
        <video
          ref={videoRef}
          className="h-full w-full object-cover"
          playsInline
          muted
        />
        {/* Aiming guide overlay */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-40 w-72 rounded-xl border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.4)]" />
        </div>
        {error && (
          <div className="absolute inset-x-4 bottom-6 rounded-lg bg-red-600/90 p-3 text-sm">
            {error}
          </div>
        )}
        {!error && (
          <p className="absolute inset-x-0 bottom-6 text-center text-sm text-white/80">
            Point the camera at the product barcode.
          </p>
        )}
      </div>
    </div>
  );
}
